import React, { useEffect, useState } from "react";
import { MapPin, Crosshair, Check, Search, LocateFixed, Building2, PencilLine } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import { requestLocation, openAppSettings, canOpenSettings } from "../lib/geolocation";
// Same picker the apartment form uses — one copy of the Leaflet lifecycle
// (and of the stacking-context fix that keeps map panes from painting
// over the search dropdown).
import LeafletMap from "./LeafletMap";
import ApartmentSelect from "./ApartmentSelect";

// Reverse/forward geocoding is proxied through the backend (geocoding.py),
// not called directly from the client — Nominatim's usage policy caps the
// public server at ~1 req/sec and requires a valid identifying User-Agent,
// neither of which is enforceable from thousands of independent end-user
// devices. Calling it directly from here got the shared network IP blocked
// outright (403 "Access denied"). See backend/geocoding.py for the fix.
async function reverseGeocode(lat, lng) {
  try {
    const { data: j } = await api.get("/api/public/geocode/reverse", { params: { lat, lon: lng } });
    const a = j.address || {};
    return {
      line1: [a.house_number, a.road || a.pedestrian || a.neighbourhood].filter(Boolean).join(" "),
      line2: [a.suburb, a.city_district].filter(Boolean).join(", "),
      city: a.city || a.town || a.village || a.county || "",
      pincode: a.postcode || "",
      display: j.display_name || "",
    };
  } catch { return null; }
}

// Unlike reverseGeocode (a silent best-effort pre-fill), this backs the
// explicit "Search" action — a real failure must be distinguishable from
// "no results", so it throws instead of swallowing. See search() below.
async function forwardGeocode(query) {
  const { data } = await api.get("/api/public/geocode/search", { params: { q: query } });
  return data.results || [];
}


export default function AddressDialog({ open, onOpenChange, onSaved, initial = null }) {
  // India centroid default; if `initial` is provided we're in EDIT mode and
  // PATCH instead of POST.
  const [form, setForm] = useState({
    label: "Home", line1: "", line2: "", city: "", pincode: "", landmark: "", instructions: "",
    lat: 17.385044, lng: 78.486671,
  });
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);
  // Permission dialog state — set to a status string only when a
  // permanent denial or hardware issue means Settings is the fix.
  // First-time denials show a lightweight toast instead so we don't
  // send new users to Settings before the OS dialog has even been shown.
  const [permIssue, setPermIssue] = useState(null);

  // ---- Serviceable Apartments ----
  // `mode` is only ever consulted when the tenant has the feature on AND has
  // at least one active apartment; otherwise the dialog renders exactly the
  // manual form it always has. Defaults to "manual" so that if the lookup
  // below fails for any reason the customer is never left unable to save.
  const [apartments, setApartments] = useState([]);
  const [aptConfig, setAptConfig] = useState({ apartments_enabled: false, manual_allowed: true, require_flat: false });
  // A typed address's own flat / house number — asked only in a store that
  // requires one (Settings → Require flat / house number).
  const [typedFlat, setTypedFlat] = useState("");
  const [mode, setMode] = useState("manual");
  const [selectedApt, setSelectedApt] = useState(null);
  const [flatNumber, setFlatNumber] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/api/customer/apartments");
        if (cancelled) return;
        setApartments(data.apartments || []);
        const cfg = {
          apartments_enabled: !!data.apartments_enabled,
          manual_allowed: data.manual_allowed !== false,
          require_flat: data.require_flat === true,
        };
        setAptConfig(cfg);
        // Editing an existing apartment address reopens in apartment mode on
        // that apartment; a legacy/manual address reopens in manual mode.
        if (initial?.apartment_id) {
          setMode("apartment");
          setSelectedApt(
            (data.apartments || []).find((a) => a.id === initial.apartment_id)
            // Keep working even if the apartment was since deactivated: fall
            // back to the name snapshotted on the address itself.
            || { id: initial.apartment_id, name: initial.apartment_name || "Selected apartment" },
          );
          setFlatNumber(initial.flat_number || "");
        } else if (initial) {
          setMode("manual");
          setTypedFlat(initial.flat_number || "");
        } else {
          // New address: default to whichever mode the tenant actually offers.
          setMode(cfg.apartments_enabled ? "apartment" : "manual");
        }
      } catch {
        // Never block address entry on this lookup.
        if (!cancelled) setAptConfig({ apartments_enabled: false, manual_allowed: true, require_flat: false });
      }
    })();
    return () => { cancelled = true; };
  }, [open, initial]);

  const useCurrentLocation = async () => {
    setLocating(true);
    let r;
    try {
      r = await requestLocation();
    } catch {
      setLocating(false);
      toast.error("Couldn't get your location. Please fill in the address manually.");
      return;
    }
    setLocating(false);
    if (r.status === "granted" && r.coords) {
      const { lat, lng } = r.coords;
      setForm((f) => ({ ...f, lat, lng }));
      const rg = await reverseGeocode(lat, lng);
      if (rg) {
        setForm((f) => ({
          ...f,
          line1: rg.line1 || f.line1,
          line2: rg.line2 || f.line2,
          city: rg.city || f.city,
          pincode: rg.pincode || f.pincode,
        }));
        toast.success("Picked up your current location");
      } else {
        toast.success("Location set — fill the rest in");
      }
      return;
    }
    // Distinguish soft (first-time) denial from permanent denial.
    // Only permanent denial routes to Settings — otherwise the user
    // sees a soft nudge and can tap the button again to get the OS
    // prompt (or the browser prompt) once more.
    if (r.status === "denied" && !r.permanent) {
      toast("Tap the location button again if you missed the prompt, or fill in the address manually.", {
        duration: 5000,
      });
      return;
    }
    setPermIssue(r.status);
  };

  const handleOpenSettings = async () => {
    const opened = await openAppSettings();
    if (!opened) {
      toast("Go to Settings → Apps → this app → Permissions → Location, then return here.", {
        duration: 6000,
      });
    }
    setPermIssue(null);
  };

  // Sync form with `initial` when the dialog opens in edit mode.
  useEffect(() => {
    if (open && initial) {
      setForm({
        label: initial.label || "Home",
        line1: initial.line1 || "",
        line2: initial.line2 || "",
        city: initial.city || "",
        pincode: initial.pincode || "",
        landmark: initial.landmark || "",
        instructions: initial.instructions || "",
        lat: initial.lat ?? 17.385044,
        lng: initial.lng ?? 78.486671,
      });
    } else if (open && !initial) {
      setForm({
        label: "Home", line1: "", line2: "", city: "", pincode: "", landmark: "", instructions: "",
        lat: 17.385044, lng: 78.486671,
      });
    }
  }, [open, initial]);

  const reset = () => {
    setForm({
      label: "Home", line1: "", line2: "", city: "", pincode: "", landmark: "", instructions: "",
      lat: 17.385044, lng: 78.486671,
    });
    setTypedFlat("");
  };

  // Try to auto-locate user on open — but only in CREATE mode; edit mode
  // keeps the saved lat/lng. Never surfaces UI on failure — this is an
  // opportunistic pre-fill, the explicit "Use my current location" button
  // is the primary path with proper permission handling.
  useEffect(() => {
    if (!open || initial) return;
    let cancelled = false;
    (async () => {
      let r;
      try {
        r = await requestLocation({ timeoutMs: 6000 });
      } catch {
        return; // opportunistic pre-fill — fail silently, no UI to surface an error to
      }
      if (cancelled || r.status !== "granted" || !r.coords) return;
      const { lat, lng } = r.coords;
      setForm((f) => ({ ...f, lat, lng }));
      const rg = await reverseGeocode(lat, lng);
      if (rg) setForm((f) => ({
        ...f,
        line1: rg.line1 || f.line1,
        line2: rg.line2 || f.line2,
        city: rg.city || f.city,
        pincode: rg.pincode || f.pincode,
      }));
    })();
    return () => { cancelled = true; };
  }, [open, initial]);

  const onMapChange = async (lat, lng) => {
    setForm((f) => ({ ...f, lat, lng }));
    const rg = await reverseGeocode(lat, lng);
    if (rg) setForm((f) => ({
      ...f,
      line1: rg.line1 || f.line1,
      line2: rg.line2 || f.line2,
      city: rg.city || f.city,
      pincode: rg.pincode || f.pincode,
    }));
  };

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const results = await forwardGeocode(query);
      setSuggestions(results);
      if (results.length === 0) toast("No matches found. Try a different search.");
    } catch (e) {
      setSuggestions([]);
      toast.error(e?.response?.data?.detail || "Search failed. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  const pickSuggestion = async (s) => {
    const lat = parseFloat(s.lat), lng = parseFloat(s.lon);
    setForm((f) => ({ ...f, lat, lng }));
    setSuggestions([]); setQuery(s.display_name);
    const rg = await reverseGeocode(lat, lng);
    if (rg) setForm((f) => ({ ...f, line1: rg.line1 || f.line1, line2: rg.line2 || f.line2, city: rg.city || f.city, pincode: rg.pincode || f.pincode }));
  };

  const save = async () => {
    const apartmentMode = aptConfig.apartments_enabled && mode === "apartment";
    if (apartmentMode) {
      if (!selectedApt) { toast.error("Please select your apartment"); return; }
      if (!flatNumber.trim()) { toast.error("Please enter your flat / house number"); return; }
    } else if (!form.line1 || !form.city || !form.pincode) {
      toast.error("Please fill line1, city and pincode (you can also drop the pin on the map)");
      return;
    } else if (aptConfig.require_flat && !typedFlat.trim()) {
      toast.error("Please enter your flat / house number");
      return;
    }
    // In apartment mode the server fills line1/city/pincode/coords from the
    // apartment itself — sending them would be ignored anyway, so send only
    // what's actually the customer's: which block, which flat, and the
    // free-text bits that apply to both modes.
    const payload = apartmentMode
      ? {
          label: form.label,
          apartment_id: selectedApt.id,
          flat_number: flatNumber.trim(),
          landmark: form.landmark,
          instructions: form.instructions,
        }
      : { ...form, address_type: "manual", ...(aptConfig.require_flat ? { flat_number: typedFlat.trim() } : {}) };
    setBusy(true);
    try {
      if (initial) {
        await api.patch(`/api/customer/addresses/${initial.id}`, payload);
      } else {
        await api.post("/api/customer/addresses", payload);
      }
      toast.success(initial ? "Address updated" : "Address saved");
      onSaved?.();
      onOpenChange(false);
      reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save address");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="address-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{initial ? "Edit address" : "New address"}</DialogTitle></DialogHeader>

        <div className="space-y-3">
          {/* Mode switch — only when the tenant actually offers a choice.
              With apartments off (the default for every existing tenant)
              nothing below changes at all. */}
          {aptConfig.apartments_enabled && aptConfig.manual_allowed && (
            <div className="grid grid-cols-2 gap-2" data-testid="address-mode-switch">
              <button onClick={() => setMode("apartment")}
                      className={`h-11 rounded-2xl text-sm font-bold border-2 inline-flex items-center justify-center gap-1.5 ${mode === "apartment" ? "border-brand-sky bg-brand-sky/10 text-brand-sky" : "border-neutral-200 bg-white text-neutral-700"}`}
                      data-testid="address-mode-apartment">
                <Building2 className="w-4 h-4"/> My apartment
              </button>
              <button onClick={() => setMode("manual")}
                      className={`h-11 rounded-2xl text-sm font-bold border-2 inline-flex items-center justify-center gap-1.5 ${mode === "manual" ? "border-brand-sky bg-brand-sky/10 text-brand-sky" : "border-neutral-200 bg-white text-neutral-700"}`}
                      data-testid="address-mode-manual">
                <PencilLine className="w-4 h-4"/> Other address
              </button>
            </div>
          )}

          {/* Apartment-only mode: no switch to show, but say why. */}
          {aptConfig.apartments_enabled && !aptConfig.manual_allowed && (
            <div className="rounded-2xl bg-brand-cream px-4 py-3 text-xs text-neutral-600" data-testid="apartment-only-note">
              We currently deliver only to the apartments listed below.
            </div>
          )}

          {aptConfig.apartments_enabled && mode === "apartment" ? (
            <div className="space-y-3" data-testid="apartment-picker">
              <div className="grid grid-cols-3 gap-2">
                {["Home", "Office", "Other"].map((l) => (
                  <button key={l} onClick={() => setForm({ ...form, label: l })}
                          className={`h-10 rounded-2xl text-sm font-bold border-2 ${form.label === l ? "border-brand-sky bg-brand-sky/10 text-brand-sky" : "border-neutral-200 bg-white text-neutral-700"}`}
                          data-testid={`apt-addr-label-${l.toLowerCase()}`}>{l}</button>
                ))}
              </div>

              {/* Collapsed field, not an inline list — the form below has to
                  stay reachable for a tenant with dozens of apartments. */}
              <ApartmentSelect
                apartments={apartments}
                value={selectedApt}
                onChange={setSelectedApt}
                testIdPrefix="apartment-select"
              />

              {selectedApt && (
                <Input value={flatNumber} onChange={(e) => setFlatNumber(e.target.value)}
                       placeholder="Flat / House number *  (e.g. 402)"
                       className="h-12 rounded-2xl" data-testid="apartment-flat-number"/>
              )}
              <Input value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })}
                     placeholder="Delivery instructions (gate code, dog etc.)"
                     className="h-12 rounded-2xl" data-testid="apartment-instructions"/>

              <Button onClick={save} disabled={busy}
                      className="w-full h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold"
                      data-testid="save-apartment-address-btn">
                <Check className="w-4 h-4 mr-2"/> Save address
              </Button>
            </div>
          ) : (
          <>
          {/* Search bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }}
              placeholder="Search area, locality or pincode…"
              className="pl-9 h-12 rounded-2xl"
              data-testid="addr-search-input"
            />
            {suggestions.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-neutral-200 rounded-2xl shadow-lg max-h-56 overflow-y-auto">
                {suggestions.map((s) => (
                  <button key={s.place_id} onClick={() => pickSuggestion(s)} className="w-full text-left p-3 hover:bg-neutral-50 border-b border-neutral-100 text-sm">
                    {s.display_name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <LeafletMap lat={form.lat} lng={form.lng} onChange={onMapChange} />

          {/* Explicit Current Location CTA — prefills the form via reverse-
              geocode AFTER a user gesture (more reliable than auto-fire). */}
          <button onClick={useCurrentLocation} disabled={locating}
                  className="w-full h-12 rounded-2xl border-2 border-dashed border-brand-sky/40 bg-brand-sky/5 text-brand-sky font-extrabold text-sm inline-flex items-center justify-center gap-2 active:scale-[0.99] transition"
                  data-testid="use-current-location-btn">
            <LocateFixed className={`w-4 h-4 ${locating ? "animate-pulse" : ""}`}/>
            {locating ? "Picking up your location…" : "Use my current location"}
          </button>

          <div className="flex items-center gap-2 text-xs text-neutral-500">
            <Crosshair className="w-3 h-3"/>
            <span>Drag the pin or tap on the map to fine-tune location</span>
          </div>

          {/* Form */}
          <div className="grid grid-cols-3 gap-2">
            {["Home", "Office", "Other"].map((l) => (
              <button
                key={l}
                onClick={() => setForm({ ...form, label: l })}
                className={`h-10 rounded-2xl text-sm font-bold border-2 ${form.label === l ? "border-brand-sky bg-brand-sky/10 text-brand-sky" : "border-neutral-200 bg-white text-neutral-700"}`}
                data-testid={`addr-label-${l.toLowerCase()}`}
              >{l}</button>
            ))}
          </div>

          {aptConfig.require_flat && (
            <Input value={typedFlat} onChange={(e) => setTypedFlat(e.target.value)}
                   placeholder="Flat / House no. *" className="h-12 rounded-2xl" data-testid="addr-flat-number"/>
          )}
          <Input value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })}
                 placeholder={aptConfig.require_flat ? "Building, Street *" : "Flat / House no., Street *"} className="h-12 rounded-2xl" data-testid="addr-line1"/>
          <Input value={form.line2} onChange={(e) => setForm({ ...form, line2: e.target.value })}
                 placeholder="Apartment / Society (optional)" className="h-12 rounded-2xl" data-testid="addr-line2"/>
          <div className="grid grid-cols-2 gap-2">
            <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                   placeholder="City *" className="h-12 rounded-2xl" data-testid="addr-city"/>
            <Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })}
                   placeholder="Pincode *" className="h-12 rounded-2xl" data-testid="addr-pincode"/>
          </div>
          <Input value={form.landmark} onChange={(e) => setForm({ ...form, landmark: e.target.value })}
                 placeholder="Landmark (optional)" className="h-12 rounded-2xl" data-testid="addr-landmark"/>
          <Input value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })}
                 placeholder="Delivery instructions (gate code, dog etc.)" className="h-12 rounded-2xl" data-testid="addr-instructions"/>

          <Button onClick={save} disabled={busy} className="w-full h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold" data-testid="save-address-btn">
            <Check className="w-4 h-4 mr-2"/> Save address
          </Button>
          </>
          )}
        </div>
      </DialogContent>

      {/* Location-permission dialog — matches native iOS / Android
          Alert style. Shows only when the user tapped "Use my current
          location" and the OS said no (or hardware couldn't get a fix).
          Two clear actions: send them to Settings or fall back to
          typing the address manually. */}
      <Dialog open={!!permIssue} onOpenChange={(o) => { if (!o) setPermIssue(null); }}>
        <DialogContent className="max-w-sm" data-testid="location-permission-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-brand-sky" />
              {permIssue === "unavailable" ? "Couldn't get your location"
                : permIssue === "restricted" ? "Location is restricted"
                : permIssue === "unsupported" ? "Location not available"
                : "Enable location access"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-700 leading-relaxed">
            {permIssue === "unavailable" && (
              "We couldn't fetch a GPS fix — check that your device's location services are on, or try again from an area with a better signal. Meanwhile, you can enter the address manually."
            )}
            {permIssue === "restricted" && (
              "Location access is blocked by a device policy (parental controls or work profile). Please enter the address manually — we can't override this from inside the app."
            )}
            {permIssue === "unsupported" && (
              "Your browser or device doesn't expose location. Please enter the address manually."
            )}
            {permIssue === "denied" && (
              "Location was previously turned off for this app. Open Settings → Permissions → Location and set it to \"Allow while using the app\", then come back and try again."
            )}
            {permIssue === "prompt" && (
              "Location access wasn't granted. Please try again — you'll see the Android / iOS prompt. If it doesn't appear, open Settings to enable it."
            )}
          </p>
          <div className="flex flex-col gap-2 mt-4">
            {(permIssue === "denied" || permIssue === "prompt") && canOpenSettings() && (
              <Button onClick={handleOpenSettings}
                      className="w-full h-11 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold"
                      data-testid="open-settings-btn">
                Open Settings
              </Button>
            )}
            {(permIssue === "denied" || permIssue === "prompt") && !canOpenSettings() && (
              <Button onClick={useCurrentLocation}
                      className="w-full h-11 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold"
                      data-testid="try-again-btn">
                Try again
              </Button>
            )}
            <Button onClick={() => setPermIssue(null)} variant="outline"
                    className="w-full h-11 rounded-2xl font-bold"
                    data-testid="enter-manually-btn">
              Enter Address Manually
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
